/**
 * Watcher Ecosystem mobile utility rail.
 * Keeps third-party storefront controls reachable without allowing them to overlap.
 * Desktop layouts and all tracking behavior are intentionally untouched.
 * Floating contact controls also yield while the native Meshberg contact form
 * is visible, preventing them from covering form fields on small screens.
 * Version: 2026-09-01
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  var STYLE_ID = 'watcher-mobile-utility-rail-style';
  var SHADOW_STYLE_ID = 'watcher-mobile-utility-shadow-style';
  var observer;
  var contactFormObserver;
  var observedContactForm;
  var scheduled = false;

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function ensureDocumentStyle() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '@media (max-width: 767px) {',
      '  html.watcher-mobile-utilities #MainContent { padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px)); }',
      '  html.watcher-contact-form-visible gty-whatsapp-chat-button,',
      '  html.watcher-contact-form-visible shopify-chat { opacity: 0 !important; visibility: hidden !important; pointer-events: none !important; }',
      '  shopify-chat { --shopify-chat-activator-offset: calc(16px + env(safe-area-inset-bottom, 0px)) !important; }',
      '  .doubly-float { left: 12px !important; right: auto !important; bottom: calc(14px + env(safe-area-inset-bottom, 0px)) !important; transform: scale(.92) !important; transform-origin: left bottom !important; }',
      '  body:not(.show-enable-toolbar) #enable-toolbar { position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; max-width: 100vw !important; max-height: 100vh !important; overflow: visible !important; pointer-events: none !important; transform: none !important; z-index: 2147483000 !important; }',
      '  body:not(.show-enable-toolbar) #enable-toolbar #enable-toolbar-content { position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; max-width: 100vw !important; max-height: 100vh !important; overflow: visible !important; visibility: hidden !important; pointer-events: none !important; transform: none !important; }',
      '  body:not(.show-enable-toolbar) #enable-toolbar #enable-toolbar-trigger { display: flex !important; visibility: visible !important; opacity: 1 !important; position: fixed !important; left: auto !important; right: 16px !important; top: auto !important; bottom: calc(76px + env(safe-area-inset-bottom, 0px)) !important; width: 48px !important; height: 48px !important; min-width: 48px !important; min-height: 48px !important; transform: none !important; pointer-events: auto !important; z-index: 2147483001 !important; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function injectShadowStyle(host, css) {
    if (!host || !host.shadowRoot || host.shadowRoot.getElementById(SHADOW_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = css;
    host.shadowRoot.appendChild(style);
  }

  function placeUtilities() {
    scheduled = false;
    ensureDocumentStyle();

    if (!isMobile()) {
      document.documentElement.classList.remove('watcher-mobile-utilities');
      document.documentElement.classList.remove('watcher-contact-form-visible');
      return;
    }

    var chat = document.querySelector('shopify-chat');
    var whatsapp = document.querySelector('gty-whatsapp-chat-button');
    var accessibility = document.getElementById('enable-toolbar-trigger');

    if (chat || whatsapp || accessibility || document.querySelector('.doubly-float')) {
      document.documentElement.classList.add('watcher-mobile-utilities');
    }

    injectShadowStyle(chat, [
      '@media (max-width: 767px) {',
      '  .activator-nudge { right: 16px !important; }',
      '  .activator { width: 48px !important; height: 48px !important; min-width: 48px !important; min-height: 48px !important; padding: 0 !important; border-radius: 999px !important; justify-content: center !important; }',
      '  .activator__label { display: none !important; }',
      '}',
    ].join('\n'));

    injectShadowStyle(whatsapp, [
      '@media (max-width: 767px) {',
      '  .gty-wacb-wrapper { left: auto !important; right: 16px !important; bottom: calc(136px + env(safe-area-inset-bottom, 0px)) !important; transform: scale(.82) !important; transform-origin: right bottom !important; }',
      '}',
    ].join('\n'));

    observeContactForm();
  }

  function observeContactForm() {
    var contactForm = document.querySelector('.meshberg-contact-form');

    if (!contactForm || !('IntersectionObserver' in window)) return;
    if (contactForm === observedContactForm && contactFormObserver) return;

    if (contactFormObserver) contactFormObserver.disconnect();
    observedContactForm = contactForm;
    contactFormObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var formIsVisible = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.08);
      document.documentElement.classList.toggle('watcher-contact-form-visible', isMobile() && formIsVisible);
    }, { threshold: [0, 0.08, 0.25] });
    contactFormObserver.observe(contactForm);
  }

  function schedulePlacement() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(placeUtilities);
  }

  ensureDocumentStyle();
  schedulePlacement();
  window.addEventListener('load', schedulePlacement, { once: true });
  window.addEventListener('resize', schedulePlacement);

  if ('MutationObserver' in window) {
    observer = new MutationObserver(schedulePlacement);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () {
      if (observer) observer.disconnect();
    }, 20000);
  }

  ['shopify-chat', 'gty-whatsapp-chat-button'].forEach(function (tagName) {
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined(tagName).then(schedulePlacement);
    }
  });
}());
